import {AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {getPublicSettings} from '@store/common';
import {UserRole, UserRoles} from '@shared/enums/user-role.enum';
import {SplashScreenService} from '@core/services/splash-screen.service';
import {UtilsService} from '@core/services/utils.service';
import * as _ from 'lodash';
import {filter, take} from 'rxjs/operators';
import {LocationsCollapse} from '@shared/enums/locations.enum';
import {getUser} from '@core/states/user.state';
import {UsersService} from '@main/admin/users/users.service';
import {NgxRolesService} from 'ngx-permissions';
import {HtmlSanitizerService} from '@core/services/html-sanitizer.service';

@Component({
    selector: 'oct-user-dialog',
    templateUrl: './user-dialog.component.html',
    styleUrls: ['./user-dialog.component.scss']
})

export class UserDialogComponent implements OnInit, AfterViewInit {
    @ViewChild('firstInput', {static: true}) firstInput: ElementRef;

    form: UntypedFormGroup;
    firstName: UntypedFormControl;
    lastName: UntypedFormControl;
    email: UntypedFormControl;
    userRole: UntypedFormControl;
    jobTitles: UntypedFormControl;
    permissions: UntypedFormControl;
    userRoles: UserRoles = UserRole;
    userRoleOptions: string[] = [UserRole.Viewer, UserRole.Editor, UserRole.Admin];
    userPermissions: any[] = [];
    jobTitle: any[] = [];
    loggedInUser: any;
    isAdmin: boolean;
    isNewUser = false;
    formDisabled = false;
    private maxLengthForObjectName: number = 150;
    octomizeChecked: boolean;

    constructor(
        public dialogRef: MatDialogRef<UserDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder,
        private store: Store<any>,
        private splashScreenService: SplashScreenService,
        private utilsService: UtilsService,
        private usersService: UsersService,
        private rolesService: NgxRolesService,
        private htmlSanitizer: HtmlSanitizerService,
    ) {
        dialogRef.disableClose = true;
    }

    ngOnInit() {
        this.isAdmin = !!this.rolesService.getRole('ADMIN');
        this.isNewUser = !this.data;
        this.formDisabled = this.data?.STATUS === 'NO';
        this.store
            .pipe(
                select(getPublicSettings),
                take(1),
            )
            .subscribe(publicSettings => {
                this.jobTitle = publicSettings.jobTitle
                    .split(';')
                    .sort((a, b) => (a.trim() > b.trim() ? 1 : -1))
                    .map((item, index) => {
                        return {
                            id: index,
                            name: item.trim(),
                            checked: false
                        };
                    });

                this.userPermissions = publicSettings.showModules
                    .split(';')
                    .filter(a => a)
                    .map(item => {
                        return {
                            id: 1,
                            title: LocationsCollapse[item],
                            value: item,
                            checked: false
                        };
                    });
            });
    }

    ngAfterViewInit(): void {
        this.store
            .pipe(
                select(getUser),
                filter(user => !!user),
                take(1),
            )
            .subscribe(user => {
                this.loggedInUser = user;
                this.createFormControls();
                this.createForm();
            });

        this.form.controls.userPermissions?.valueChanges.subscribe((value) => {
            this.octomizeChecked = value.find(item => item.value === 'octomize').checked;
        });
    }

    public setDisabled() {
        if (this.data?.ID) {
            return 'disabled';
        }
        return null;
    }

    public setDisabledAfterLogin() {
        if (this.data?.ID) {
            return 'disabled';
        }
        return null;
    }

    public onSubmit(): void {
        console.log('onSubmit', this.form);
        this.splashScreenService.setLoaderOn();
        this.dialogRef.close({
            form: this.form.getRawValue(),
        });
    }

    public close(): void {
        this.dialogRef.close();
    }

    onActivateUser() {
        this.formDisabled = false;
        this.form.controls.firstName.enable();
        this.form.controls.lastName.enable();
        this.form.controls.userRole.enable();
    }

    /**
     * Custom validator to prevent HTML/script injection in text fields
     */
    private noHtmlValidator(control: UntypedFormControl): { [key: string]: any } | null {
        if (!control.value) {
            return null;
        }

        const value = control.value.toString();

        // Check if the value contains HTML or dangerous patterns
        if (!this.htmlSanitizer.isSafeText(value)) {
            return {
                containsHtml: {
                    message: 'HTML tags and scripts are not allowed'
                }
            };
        }

        // Additional check for dangerous characters
        if (/<|>|javascript:|onerror|onclick/gi.test(value)) {
            return {
                dangerousContent: {
                    message: 'Invalid characters detected'
                }
            };
        }

        return null;
    }

    private createFormControls() {
        this.firstName = new UntypedFormControl({
            value: this.data?.FIRST_NAME ? this.data?.FIRST_NAME : '',
            disabled: this.formDisabled
        }, [
            Validators.required,
            Validators.maxLength(this.maxLengthForObjectName),
            this.noHtmlValidator.bind(this)
        ]);

        this.lastName = new UntypedFormControl({
            value: this.data?.LAST_NAME ? this.data?.LAST_NAME : '',
            disabled: this.formDisabled
        }, [
            Validators.required,
            Validators.maxLength(this.maxLengthForObjectName),
            this.noHtmlValidator.bind(this)
        ]);

        this.email = new UntypedFormControl({
            value: this.data?.EMAIL ? this.data?.EMAIL : '',
            disabled: !this.isNewUser || this.formDisabled
        }, [
            Validators.required,
            Validators.maxLength(this.maxLengthForObjectName),
            Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
        ]);

        if (this.isAdmin) {
            this.userRole = new UntypedFormControl({
                value: this.data?.USER_ROLE ? this.data?.USER_ROLE : '',
                disabled: this.formDisabled
            }, [
                Validators.required,
            ]);
        }

        const jobTitleArray = _.uniq(this.jobTitle.map((titleItem: any) => {
            titleItem.checked = this.data?.JOB_TITLE ? this.data?.JOB_TITLE.includes(titleItem.name) : false;
            return titleItem;
        }));

        this.jobTitles = new UntypedFormControl(jobTitleArray, (a) => {
            if (a.value.filter(item => item.checked).length !== 0) {
                return null;
            } else {
                return {
                    error: 'Job title is required'
                };
            }
        });

        if (this.isAdmin) {
            const dataPermissions = this.data?.USER_PERMISSIONS ? this.data?.USER_PERMISSIONS.map(item => item.toLowerCase()) : [];
            const userPermissionsArray = _.uniq(this.userPermissions.map((permissionItem: any) => {
                permissionItem.checked = dataPermissions ? dataPermissions.includes(permissionItem.value) : false;
                if (permissionItem.value === 'octomize') {
                    this.octomizeChecked = permissionItem.checked;
                }
                return permissionItem;
            }));
            this.permissions = new UntypedFormControl(userPermissionsArray, (a) => {
                if (a.value.filter(item => item.checked).length !== 0) {
                    return null;
                } else {
                    return {
                        error: 'User permission is required'
                    };
                }
            });
        }
    }

    private createForm() {
        let formControls;
        if (this.isAdmin) {
            formControls = {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                userRole: this.userRole,
                jobTitles: this.jobTitles,
                userPermissions: this.permissions,
            };
        } else {
            formControls = {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                jobTitles: this.jobTitles,
            };
        }

        this.form = new UntypedFormGroup({...formControls});
    }
}

